import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import type { CajaMovimiento, CajaReporte, CajaReporteTotals, ReportFormat } from '@/application/metrics';
import { aggregateCajaReportes, buildFinancesReturnHref } from '@/application/metrics';
import { downloadCajaReport, getCajaMovimientos, getCajaReporte } from '@/infrastructure/http/metricsApi';
import { CajaReporteBlock } from '@/presentation/components/metrics';
import { useClinicNames } from '@/presentation/hooks/useClinicNames';
import { useAuth } from '@/presentation/contexts/useAuth';
import { usePermissions } from '@/presentation/contexts/permissionsContextCore';
import { Icon } from '@/presentation/components/common/Icon';
import {
  ActiveFilterChips,
  FilterSheet,
  ResponsiveDataList,
  type FilterFieldConfig,
  type ResponsiveColumnDef,
} from '@/presentation/components/common';
import { MultiSelectFilter, type MultiSelectOption } from '@/presentation/components/common/MultiSelectFilter';
import { formatCurrency } from '@/presentation/components/metrics/metricFormatters';
import { AccessDenied } from '@/presentation/components/common/AccessDenied';
import { Breadcrumbs } from '@/presentation/components/common/Breadcrumbs';
import { firstVisibleHousePath } from '@/application/navigation/menuHouses';
import { canExportReports } from '@/application/permissions/canExportReports';
import { useGlobalPeriod } from '@/presentation/hooks/useGlobalPeriod';

type CajaTab = 'todos' | 'ingreso' | 'egreso';

interface ActiveFilterChip {
  id: string;
  label: string;
  onRemove: () => void;
}

interface CajaMovimientoDetalle extends CajaMovimiento {
  tenantId: string;
}

// Sentinel para representar valores vacíos/nulos en los filtros (los que se ven como "—").
const EMPTY = '__empty__';

function encodeMulti(values: string[]): string {
  return values.length > 0 ? JSON.stringify(values) : '';
}

function decodeMulti(raw: string | undefined): string[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw) as unknown;
    return Array.isArray(parsed) ? parsed.map(String) : [];
  } catch {
    return [];
  }
}

function buildOptions(values: Array<string | null>, emptyLabel: string): MultiSelectOption[] {
  const distinct = new Set<string>();
  let hasEmpty = false;
  for (const value of values) {
    if (value == null || value === '') {
      hasEmpty = true;
    } else {
      distinct.add(value);
    }
  }
  const options = [...distinct].sort((a, b) => a.localeCompare(b)).map((value) => ({ value, label: value }));
  if (hasEmpty) {
    options.push({ value: EMPTY, label: emptyLabel });
  }
  return options;
}

function matches(value: string | null, selected: string[]): boolean {
  if (selected.length === 0) {
    return true;
  }
  return selected.includes(value == null || value === '' ? EMPTY : value);
}

function labelFor(options: MultiSelectOption[], value: string): string {
  return options.find((option) => option.value === value)?.label ?? value;
}

function parseCajaTab(raw: string | null): CajaTab {
  if (raw === 'ingreso' || raw === 'egreso' || raw === 'todos') return raw;
  return 'todos';
}

/** Pantalla de detalle de movimientos de caja (grilla amplia + filtros). Se llega desde Finanzas / ojo de Caja. */
export function CajaDetailPage() {
  const { t, i18n } = useTranslation();
  const { token, user } = useAuth();
  const { hasPermission } = usePermissions();
  const clinicNames = useClinicNames();
  const [params, setSearchParams] = useSearchParams();

  const canView = hasPermission('metrics.caja') || canExportReports(hasPermission);

  const tenantId = params.get('tenant') ?? '';
  const isAllClinics = tenantId === 'all';
  const canExport = canExportReports(hasPermission) && !isAllClinics;
  const { period, year, month } = useGlobalPeriod();
  const consultorioId = params.get('sede') ? Number(params.get('sede')) : undefined;
  const tipoFromQuery = parseCajaTab(params.get('tipo'));
  const allowedTenantIds = user?.scope.allowedTenantIds ?? [];
  const allowedTenantIdsKey = allowedTenantIds.join('|');
  const tenantInScope =
    !tenantId || isAllClinics || allowedTenantIds.length === 0 || allowedTenantIds.includes(tenantId);
  // Subconjunto explícito de clínicas (consolidado de solo estas), validado contra el scope.
  const subsetParam = params.get('tenants') ?? '';
  const targetTenantIds = useMemo(() => {
    if (!tenantId || !tenantInScope) {
      return [];
    }
    if (isAllClinics) {
      const allowed = allowedTenantIdsKey ? allowedTenantIdsKey.split('|') : [];
      if (subsetParam) {
        const subset = subsetParam.split(',').filter((id) => allowed.includes(id));
        return subset.length > 0 ? subset : allowed;
      }
      return allowed;
    }
    return [tenantId];
  }, [allowedTenantIdsKey, isAllClinics, subsetParam, tenantId, tenantInScope]);

  const [movimientos, setMovimientos] = useState<CajaMovimientoDetalle[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<CajaTab>(tipoFromQuery);

  useEffect(() => {
    setTab(tipoFromQuery);
  }, [tipoFromQuery]);

  const handleTabChange = useCallback(
    (next: CajaTab) => {
      setTab(next);
      setSearchParams(
        (prev) => {
          const nextParams = new URLSearchParams(prev);
          if (next === 'todos') nextParams.delete('tipo');
          else nextParams.set('tipo', next);
          return nextParams;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const financesBackHref = useMemo(() => {
    if (!year || !month) return '/finances';
    if (isAllClinics) {
      return buildFinancesReturnHref({
        year,
        month,
        period,
        tab: 'network',
      });
    }
    if (!tenantId || !tenantInScope) return '/finances';
    return buildFinancesReturnHref({
      tenantId,
      year,
      month,
      period,
      sede: consultorioId,
      tab: 'overview',
    });
  }, [consultorioId, isAllClinics, month, period, tenantId, tenantInScope, year]);
  const [conceptos, setConceptos] = useState<string[]>([]);
  const [clinicas, setClinicas] = useState<string[]>([]);
  const [sedes, setSedes] = useState<string[]>([]);
  const [formas, setFormas] = useState<string[]>([]);
  const [reporte, setReporte] = useState<CajaReporteTotals | null>(null);
  const [filtersOpen, setFiltersOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!token || targetTenantIds.length === 0 || !year || !month) {
      return;
    }
    setLoading(true);
    setError(null);
    Promise.all(
      targetTenantIds.map(async (targetTenantId) => {
        const rows = await getCajaMovimientos(token, {
          tenantId: targetTenantId,
          year,
          month,
          consultorioId: isAllClinics ? undefined : consultorioId,
          limit: 1000,
        });
        return rows.map((row) => ({
          ...row,
          tenantId: targetTenantId,
        }));
      })
    )
      .then((rows) => {
        if (!cancelled) {
          setMovimientos(rows.flat());
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setMovimientos([]);
          setError(err instanceof Error ? err.message : t('metrics.states.error.message'));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [consultorioId, isAllClinics, month, t, targetTenantIds, token, year]);

  // Reporte del período (totales + comisiones por forma de pago). Es independiente de los
  // filtros de la grilla: siempre refleja el período/sede completos.
  const [reporteError, setReporteError] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    if (!token || targetTenantIds.length === 0 || !year || !month) {
      return;
    }
    setReporteError(null);
    Promise.all(
      targetTenantIds.map((targetTenantId) =>
        getCajaReporte(token, {
          tenantId: targetTenantId,
          year,
          month,
          consultorioId: isAllClinics ? undefined : consultorioId,
        })
      )
    )
      .then((reportes: CajaReporte[]) => {
        if (!cancelled) {
          setReporte(aggregateCajaReportes(reportes));
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setReporte(null);
          setReporteError(err instanceof Error ? err.message : t('metrics.states.error.message'));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [consultorioId, isAllClinics, month, t, targetTenantIds, token, year]);

  // La exportación es por clínica, así que en el consolidado no se ofrece.
  const handleExport = useCallback(
    (format: ReportFormat) =>
      downloadCajaReport(token ?? '', { tenantId, year, month, consultorioId }, format),
    [consultorioId, month, tenantId, token, year]
  );

  const blank = t('metrics.caja.detail.blank');
  const clinicLabel = useCallback((movimiento: CajaMovimientoDetalle) => clinicNames[movimiento.tenantId] ?? movimiento.tenantId, [clinicNames]);
  const conceptoOptions = useMemo(() => buildOptions(movimientos.map((m) => m.concepto), blank), [movimientos, blank]);
  const clinicaOptions = useMemo(() => buildOptions(movimientos.map(clinicLabel), blank), [blank, clinicLabel, movimientos]);
  const sedeOptions = useMemo(() => buildOptions(movimientos.map((m) => m.sede), blank), [movimientos, blank]);
  const formaOptions = useMemo(() => buildOptions(movimientos.map((m) => m.formaPago), blank), [movimientos, blank]);

  const filtered = useMemo(
    () =>
      movimientos.filter(
        (movimiento) =>
          (tab === 'todos' || (tab === 'ingreso') === movimiento.esIngreso) &&
          matches(movimiento.concepto, conceptos) &&
          matches(clinicLabel(movimiento), clinicas) &&
          matches(movimiento.sede, sedes) &&
          matches(movimiento.formaPago, formas)
      ),
    [movimientos, tab, conceptos, clinicas, sedes, formas, clinicLabel]
  );

  const tabs = useMemo<Array<{ key: CajaTab; label: string }>>(
    () => [
      { key: 'todos', label: t('metrics.caja.detail.all') },
      { key: 'ingreso', label: t('metrics.caja.income') },
      { key: 'egreso', label: t('metrics.caja.expenses') },
    ],
    [t]
  );

  const activeFilterChips = useMemo<ActiveFilterChip[]>(() => {
    const chips: ActiveFilterChip[] = [];
    const addSelectedChips = (
      group: string,
      selected: string[],
      options: MultiSelectOption[],
      setSelected: (values: string[]) => void
    ) => {
      selected.forEach((value) => {
        const optionLabel = labelFor(options, value);
        chips.push({
          id: `${group}:${value}`,
          label: `${group}: ${optionLabel}`,
          onRemove: () => setSelected(selected.filter((item) => item !== value)),
        });
      });
    };

    if (tab !== 'todos') {
      chips.push({
        id: `tipo:${tab}`,
        label: `${t('metrics.caja.detail.type')}: ${tabs.find((option) => option.key === tab)?.label ?? tab}`,
        onRemove: () => handleTabChange('todos'),
      });
    }
    addSelectedChips(t('metrics.caja.detail.concept'), conceptos, conceptoOptions, setConceptos);
    if (isAllClinics) {
      addSelectedChips(t('metrics.caja.detail.clinic'), clinicas, clinicaOptions, setClinicas);
    }
    addSelectedChips(t('metrics.caja.detail.site'), sedes, sedeOptions, setSedes);
    addSelectedChips(t('metrics.caja.detail.method'), formas, formaOptions, setFormas);

    return chips;
  }, [
    clinicaOptions,
    clinicas,
    conceptoOptions,
    conceptos,
    formaOptions,
    formas,
    handleTabChange,
    isAllClinics,
    sedeOptions,
    sedes,
    t,
    tab,
    tabs,
  ]);

  const sheetValues = useMemo(
    () => ({
      conceptos: encodeMulti(conceptos),
      clinicas: encodeMulti(clinicas),
      sedes: encodeMulti(sedes),
      formas: encodeMulti(formas),
    }),
    [clinicas, conceptos, formas, sedes]
  );

  const sheetEmptyValues = useMemo(
    () => ({ conceptos: '', clinicas: '', sedes: '', formas: '' }),
    []
  );

  const filterFields = useMemo<FilterFieldConfig[]>(() => {
    const multiField = (
      id: string,
      label: string,
      options: MultiSelectOption[]
    ): FilterFieldConfig => ({
      id,
      type: 'custom',
      label,
      render: ({ value, onChange }) => (
        <MultiSelectFilter
          label={label}
          options={options}
          selected={decodeMulti(value)}
          onChange={(next) => onChange(encodeMulti(next))}
        />
      ),
    });

    const fields: FilterFieldConfig[] = [
      multiField('conceptos', t('metrics.caja.detail.concept'), conceptoOptions),
    ];
    if (isAllClinics) {
      fields.push(multiField('clinicas', t('metrics.caja.detail.clinic'), clinicaOptions));
    }
    fields.push(
      multiField('sedes', t('metrics.caja.detail.site'), sedeOptions),
      multiField('formas', t('metrics.caja.detail.method'), formaOptions)
    );
    return fields;
  }, [clinicaOptions, conceptoOptions, formaOptions, isAllClinics, sedeOptions, t]);

  const columns = useMemo<ResponsiveColumnDef<CajaMovimientoDetalle>[]>(() => {
    const cols: ResponsiveColumnDef<CajaMovimientoDetalle>[] = [
      {
        key: 'fecha',
        header: t('metrics.caja.detail.date'),
        cell: (row) => new Date(row.fecha).toLocaleDateString(i18n.language),
        cellClassName: 'whitespace-nowrap text-gray-600 dark:text-gray-300',
      },
    ];
    if (isAllClinics) {
      cols.push({
        key: 'clinic',
        header: t('metrics.caja.detail.clinic'),
        cell: (row) => clinicLabel(row),
        cellClassName: 'text-gray-600 dark:text-gray-300',
      });
    }
    cols.push(
      {
        key: 'concepto',
        header: t('metrics.caja.detail.concept'),
        cell: (row) => row.concepto ?? '—',
        cellClassName: 'text-gray-800 dark:text-gray-200',
      },
      {
        key: 'sede',
        header: t('metrics.caja.detail.site'),
        cell: (row) => row.sede ?? '—',
        cellClassName: 'text-gray-600 dark:text-gray-300',
      },
      {
        key: 'medio',
        header: t('metrics.caja.detail.method'),
        cell: (row) => row.formaPago ?? '—',
        cellClassName: 'text-gray-600 dark:text-gray-300',
      },
      {
        key: 'monto',
        header: t('metrics.caja.detail.amount'),
        align: 'right',
        cell: (row) => (
          <span
            className={`font-medium tabular-nums ${
              row.esIngreso ? 'text-teal-600 dark:text-teal-400' : 'text-red-600 dark:text-red-400'
            }`}
          >
            {row.esIngreso ? '' : '-'}
            {formatCurrency(row.monto, i18n.language, 'ARS')}
          </span>
        ),
        cellClassName: 'whitespace-nowrap',
      }
    );
    return cols;
  }, [clinicLabel, i18n.language, isAllClinics, t]);

  if (!canView) {
    return <AccessDenied homeTo={firstVisibleHousePath(hasPermission)} />;
  }

  const missingScope =
    !tenantId || !year || !month || !tenantInScope || (isAllClinics && targetTenantIds.length === 0);

  return (
    <div className="panel-page space-y-4 p-4 xl:p-6" data-testid="caja-movimientos-page">
      <Breadcrumbs
        label={t('metrics.caja.detail.breadcrumbLabel')}
        items={[
          {
            label: t('breadcrumbs.finances'),
            to: financesBackHref,
            testId: 'caja-back-finances',
          },
          { label: t('breadcrumbs.movements') },
        ]}
      />

      <div className="flex flex-wrap items-center gap-3">
        <Link
          to={financesBackHref}
          className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1.5 text-sm font-semibold text-gray-700 transition hover:bg-gray-100 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
        >
          <Icon path="M15 18l-6-6 6-6" className="h-4 w-4" />
          {t('metrics.caja.detail.backToFinances')}
        </Link>
        <h1 className="text-2xl font-bold text-gray-950 dark:text-white">{t('metrics.caja.detail.title')}</h1>
      </div>

      {missingScope ? (
        <p
          className="rounded-lg border border-gray-200 bg-white p-4 text-sm text-gray-500 dark:border-gray-800 dark:bg-gray-900 dark:text-gray-400"
          data-testid="caja-movimientos-empty-scope"
        >
          {!tenantInScope && tenantId
            ? t('metrics.caja.detail.forbiddenTenant')
            : t('metrics.caja.detail.empty')}
        </p>
      ) : (
        <>
          {reporteError ? (
            <p
              role="alert"
              className="rounded-lg border border-gray-200 bg-white p-4 text-sm text-red-700 dark:border-gray-800 dark:bg-gray-900 dark:text-red-200"
            >
              {reporteError}
            </p>
          ) : (
            reporte && (
              <CajaReporteBlock data={reporte} onExport={canExport ? handleExport : undefined} />
            )
          )}

          <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900">
            <div className="mb-3 flex flex-wrap items-center gap-2">
              {tabs.map((option) => (
                <button
                  key={option.key}
                  type="button"
                  onClick={() => handleTabChange(option.key)}
                  className={`rounded-md px-3 py-1 text-sm font-medium transition ${
                    tab === option.key
                      ? 'bg-teal-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
                  }`}
                >
                  {option.label}
                </button>
              ))}
              <span className="ml-auto text-xs text-gray-500 dark:text-gray-400">
                {t('metrics.caja.detail.count', { count: filtered.length })}
              </span>
            </div>

            {/* Desktop: multi-selects en fila */}
            <div
              className="mb-3 hidden flex-wrap items-center gap-2 md:flex"
              data-testid="caja-filters-desktop"
            >
              <MultiSelectFilter
                label={t('metrics.caja.detail.concept')}
                options={conceptoOptions}
                selected={conceptos}
                onChange={setConceptos}
              />
              {isAllClinics && (
                <MultiSelectFilter
                  label={t('metrics.caja.detail.clinic')}
                  options={clinicaOptions}
                  selected={clinicas}
                  onChange={setClinicas}
                />
              )}
              <MultiSelectFilter
                label={t('metrics.caja.detail.site')}
                options={sedeOptions}
                selected={sedes}
                onChange={setSedes}
              />
              <MultiSelectFilter
                label={t('metrics.caja.detail.method')}
                options={formaOptions}
                selected={formas}
                onChange={setFormas}
              />
            </div>

            {/* Mobile: bottom sheet */}
            <div className="mb-3 space-y-2 md:hidden" data-testid="caja-filters-mobile">
              <FilterSheet
                open={filtersOpen}
                onOpenChange={setFiltersOpen}
                fields={filterFields}
                values={sheetValues}
                emptyValues={sheetEmptyValues}
                onApply={(next) => {
                  setConceptos(decodeMulti(next.conceptos));
                  setClinicas(decodeMulti(next.clinicas));
                  setSedes(decodeMulti(next.sedes));
                  setFormas(decodeMulti(next.formas));
                }}
                onClearAll={() => {
                  setConceptos([]);
                  setClinicas([]);
                  setSedes([]);
                  setFormas([]);
                }}
              />
              <ActiveFilterChips
                chips={activeFilterChips.map((chip) => ({
                  id: chip.id,
                  label: chip.label,
                  onClear: chip.onRemove,
                }))}
                ariaLabel={t('metrics.caja.detail.activeFilters')}
              />
            </div>

            {/* Desktop chips (mismo set; sheet chips solo mobile) */}
            {activeFilterChips.length > 0 && (
              <div
                className="mb-3 hidden flex-wrap items-center gap-2 md:flex"
                aria-label={t('metrics.caja.detail.activeFilters')}
                data-testid="caja-filter-chips-desktop"
              >
                {activeFilterChips.map((chip) => (
                  <span
                    key={chip.id}
                    className="inline-flex h-8 max-w-full items-center gap-1.5 rounded-full border border-teal-500/40 bg-teal-50 px-2.5 text-xs font-semibold text-teal-700 dark:border-teal-400/30 dark:bg-teal-950/40 dark:text-teal-200"
                  >
                    <span className="truncate">{chip.label}</span>
                    <button
                      type="button"
                      onClick={chip.onRemove}
                      aria-label={t('metrics.caja.detail.removeFilter', { label: chip.label })}
                      className="grid h-5 w-5 shrink-0 place-items-center rounded-full text-teal-700 transition hover:bg-teal-100 focus:outline-none focus:ring-2 focus:ring-teal-500 dark:text-teal-200 dark:hover:bg-teal-900"
                    >
                      x
                    </button>
                  </span>
                ))}
              </div>
            )}

            {error ? (
              <p role="alert" className="p-4 text-sm text-red-700 dark:text-red-200">
                {error}
              </p>
            ) : (
              <div className="max-h-[70vh] overflow-auto">
                <ResponsiveDataList
                  rows={filtered}
                  keyField={(row) => `${row.tenantId}:${row.id}`}
                  columns={columns}
                  loading={loading}
                  ariaLabel={t('metrics.caja.detail.title')}
                  emptyMessage={
                    <span data-testid="caja-movimientos-empty">{t('metrics.caja.detail.empty')}</span>
                  }
                  renderCard={(row) => {
                    const amount = (
                      <span
                        className={`text-base font-semibold tabular-nums ${
                          row.esIngreso
                            ? 'text-teal-600 dark:text-teal-400'
                            : 'text-red-600 dark:text-red-400'
                        }`}
                      >
                        {row.esIngreso ? '' : '-'}
                        {formatCurrency(row.monto, i18n.language, 'ARS')}
                      </span>
                    );
                    const meta = [
                      row.sede ?? '—',
                      row.formaPago ?? '—',
                      isAllClinics ? clinicLabel(row) : null,
                    ]
                      .filter(Boolean)
                      .join(' · ');
                    return (
                      <div className="space-y-1" data-testid="caja-movimiento-card">
                        <div className="flex items-start justify-between gap-3">
                          <p className="min-w-0 text-sm font-semibold text-gray-950 dark:text-white">
                            {row.concepto ?? '—'}
                          </p>
                          {amount}
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {new Date(row.fecha).toLocaleDateString(i18n.language)}
                        </p>
                        <p className="text-xs text-gray-600 dark:text-gray-300">{meta}</p>
                      </div>
                    );
                  }}
                />
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
